import { useEffect, useState } from 'react';
import { syncDrainService } from '../services/inspectionSync/initSyncDrain';

/**
 * The pending count for the header badge: Queued, Photos pending and Needs attention
 * inspections. Deliberately NOT a Dexie liveQuery - that would re-materialise
 * every inspection record on every observation write for an always-mounted header.
 * Instead the count refreshes on the moments it can actually change or be seen:
 * mount, route change (refreshKey), reconnect, app-foreground, and drain completion.
 */
/** Fired whenever code changes a delivery state outside a drain (e.g. the submit screen
 * queuing an inspection) so the badge updates immediately - found live: the route-change
 * refresh races the submit write, and offline there is no later trigger until
 * visibility/online. */
export const PENDING_DELIVERY_CHANGED_EVENT = 'bdk:pending-delivery-changed';

export function usePendingDeliveryCount(refreshKey?: string): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      syncDrainService
        .countPending()
        .then((n) => {
          if (!cancelled) setCount(n);
        })
        .catch(() => undefined);
    };

    refresh();
    const offDrained = syncDrainService.onDrained(refresh);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('online', refresh);
    window.addEventListener(PENDING_DELIVERY_CHANGED_EVENT, refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      offDrained();
      window.removeEventListener('online', refresh);
      window.removeEventListener(PENDING_DELIVERY_CHANGED_EVENT, refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshKey]);

  return count;
}
